import { useCallback, useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '../api/http';
import { fetchSession, type SessionFull } from '../api/missions';
import {
  CARD_STEP,
  addTestStep,
  cancelTest,
  claimTest,
  closeTest,
  createTest,
  deleteTest,
  editTestStep,
  fetchTest,
  fetchTests,
  finishTest,
  importTests,
  openTestDraft,
  pageState,
  removeTestStep,
  subscribeTest,
  setTestMission,
  setTestPerformer,
  startTestServer,
  stopTestServer,
  uploadTestFile,
  type AnswerContext,
  type CreateTest,
  type ImportCard,
  type LendState,
  type StepAnswer,
  type TestAnswer,
  type TestAudience,
  type TestKind,
  type TestMedium,
  type TestList,
  type ServerBusy,
  type TestPerformer,
  type TestTask,
} from '../api/tests';
import {
  fetchMyWatcherJob,
  fetchWatcherPresence,
  queueMyWatcherJob,
  WATCHER_DOWNLOAD_URL,
  type WatcherJob,
} from '../api/watcher';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { CardServer } from './CardServer';
import { cardServerPhase } from './cardServerPhase';
import { JoinCard } from './DecisionPanels';
import { LauncherMods } from './SessionPage';
import { PageLoading } from './PageStates';
import { Shell } from './Shell';
import { errorText } from './useSessionLive';
import { ProvesInbox } from './ProvesInbox';
import { CardNotes, GameReports, StepEditor } from './TestContributions';
import { PHASE_ICON, phaseOf, stageOf } from './testPhases';
import { isAppPath, parseSteps } from './testSteps';
import { AddStep, StepWarnings } from './TestStepWording';
import { WatcherCard, WatcherDownload } from './WatcherCard';
import { loadWebCards, putWebCard } from './webCards';
import { fmtMoment } from './dates';

const when = (iso: string | null) => (iso ? fmtMoment(iso) : '');
const KINDS: TestKind[] = ['play', 'lend', 'judge'];
// A picture beside every word a tester reads (the owner, 2026-09-24): the kinds,
// the lesson's four moves, the "how" of each kind, the steps' phases.
const KIND_ICON: Record<TestKind, string> = {
  play: 'bi-controller',
  lend: 'bi-pc-display',
  judge: 'bi-question-circle',
};
const HOW_ICON = ['bi-hand-index-thumb', 'bi-pc-display', 'bi-controller', 'bi-chat-left-text'];
const WEB_HOW_TO_ICON = ['bi-box-arrow-up-right', 'bi-check2-square', 'bi-pencil'];
const HOW_TO_ICON: Record<TestKind, string[]> = {
  play: ['bi-pc-display', 'bi-door-open', 'bi-keyboard'],
  lend: ['bi-pc-display', 'bi-cup-hot'],
  judge: ['bi-image'],
};

/** The steps that carry an answer: a comment alone on a step the game never answered is not one. */
const answeredSteps = (answers: TestAnswer[]) =>
  new Set(answers.filter((a) => a.answer).map((a) => a.stepId));

function answerWord(answer: string) {
  const t = ca.testsPage;
  return answer === 'yes' ? t.answerYes : answer === 'no' ? t.answerNo : t.answerCant;
}

/** Who plays the card: a person, or the agent on a lent machine. Said on every card, never guessed. */
function PerformerChip({ performer }: { performer: TestPerformer }) {
  return (
    <span className={`chip performer-${performer}`}>
      <i className={`bi ${performer === 'agent' ? 'bi-robot' : 'bi-person'}`} aria-hidden="true" />
      {ca.inbox.performers[performer]}
    </span>
  );
}

function KindChip({ kind }: { kind: TestKind }) {
  const t = ca.testsPage;
  return (
    <span className={`chip kind-${kind}`}>
      <i className={`bi ${KIND_ICON[kind]}`} aria-hidden="true" />
      {t.kinds[kind]}
    </span>
  );
}

/** A card whose steps are done in the browser: said on the card, and what the «Web» filter keeps. */
function WebChip() {
  return (
    <span className="chip medium-web">
      <i className="bi bi-globe2" aria-hidden="true" />
      {ca.testsPage.web.chip}
    </span>
  );
}

/** Where a web card's answer was given from, in one line, for whoever reviews it. */
function contextLine(context: AnswerContext | null | undefined): string {
  if (!context) return '';
  const size =
    context.client?.width && context.client?.height
      ? `${context.client.width}×${context.client.height}`
      : '';
  const version = context.server?.platformVersion
    ? ca.inbox.version(context.server.platformVersion)
    : '';
  return ca.testsPage.web.context(context.client?.path ?? '', size, version);
}

/** Where the tester is on the task's own road, from claiming it to the review. */
function Stepper({
  kind,
  web = false,
  state,
  steps,
  answered,
}: {
  kind: TestKind;
  web?: boolean;
  state: string;
  steps: number;
  answered: number;
}) {
  const t = ca.testsPage;
  const stage = stageOf(kind, state, steps, answered);
  const names = web ? t.web.stages : (t.stages[kind] ?? []);
  const complete = state === 'closed';
  return (
    <ol className="stepper" aria-label={t.stagesLabel}>
      {names.map((name, i) => (
        <li key={name} className={complete || i < stage ? 'done' : i === stage ? 'current' : ''}>
          {name}
        </li>
      ))}
    </ol>
  );
}

/**
 * The tests (docs/design/HUMAN_TESTING_STUDY.md §6, §7). The page opens on
 * one list of every test and every review, by tray (`ProvesInbox`; the owner,
 * 2026-09-29): what waits comes first, and the lesson on how a test goes,
 * the watcher and the admin's own form sit folded under it. A card's own
 * page is unchanged: a tester's answers arrive from the game as the session's
 * own events and are shown as they come, nothing is typed twice.
 */
/** How often an open task page reads the task again. */
export const POLL_MS = 5000;

export function Proves() {
  const t = ca.testsPage;
  const { me } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState<TestList | null>(null);
  const [task, setTask] = useState<TestTask | null>(null);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = me.roles.includes('ADMIN');
  const isTester = isAdmin || me.roles.includes('TESTER');

  const load = useCallback(() => {
    fetchTests()
      .then((l) => {
        setList(l);
        setError(null);
      })
      .catch((e) => setError(errorText(e)));
  }, []);

  // The list of cards is the inbox's own call; a card's page asks only whether
  // files may be dropped on it, which the same answer carries.
  useEffect(() => {
    if (id) load();
  }, [id, load]);

  // Read again every few seconds while the page is in front: the steps answered
  // in game land here as they are given, with nobody reloading (the owner,
  // acceptance run 17, 2026-09-25).
  useEffect(() => {
    if (!id) {
      setTask(null);
      return;
    }
    let alive = true;
    const read = () =>
      fetchTest(Number(id))
        .then((next) => {
          if (alive) setTask(next);
        })
        .catch((e) => {
          if (alive) setError(errorText(e));
        });
    void read();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') void read();
    }, POLL_MS);
    // And at once when somebody writes on it, from the page or from the game (the
    // owner, 2026-09-30): what one person types is on the others' page as they type.
    const unsubscribe = subscribeTest(Number(id), () => void read());
    return () => {
      alive = false;
      window.clearInterval(timer);
      unsubscribe?.();
    };
  }, [id]);

  const onChanged = (next: TestTask) => {
    setTask(next);
    // A web card taken or finished here: the «Prova en curs» panel of the other pages follows.
    putWebCard(next);
    load();
  };
  const onSaved = (next: TestTask) => {
    setTask(next);
    putWebCard(next);
  };

  return (
    <Shell title={t.title} width="wide">
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {id && !task ? (
        !error && <PageLoading label={t.loading} />
      ) : task ? (
        <TaskDetail
          task={task}
          uploads={list?.uploads ?? false}
          isAdmin={isAdmin}
          onChanged={onChanged}
          onSaved={onSaved}
          onError={setError}
          onBack={() => navigate('/proves')}
        />
      ) : (
        <>
          <ProvesInbox isAdmin={isAdmin} onError={setError} />
          <details className="card folded">
            <summary>
              <i className="bi bi-signpost-2" aria-hidden="true" /> {ca.inbox.help}
            </summary>
            <HowItWorks />
          </details>
          <details className="card folded">
            <summary>
              <i className="bi bi-pc-display" aria-hidden="true" /> {ca.inbox.machine}
            </summary>
            <WatcherCard isAdmin={isAdmin} onError={setError} />
          </details>
          {isTester && (
            <details className="card folded">
              <summary>
                <i className="bi bi-images" aria-hidden="true" /> {ca.inbox.adminLinks}
              </summary>
              <ul className="plain">
                <li>
                  <Link to="/proves/inspeccions">{ca.inbox.allTours}</Link>
                </li>
                <li>
                  <Link to="/proves/inspeccions/resum">{ca.inbox.summary}</Link>
                </li>
              </ul>
            </details>
          )}
          {isAdmin && (
            <details className="card admin-tools">
              <summary>{t.adminTools}</summary>
              <p className="muted small">{t.adminToolsHint}</p>
              <CreateForm
                onCreated={(created) => navigate(`/proves/${created.id}`)}
                onError={setError}
              />
              <ImportCards onError={setError} />
            </details>
          )}
        </>
      )}
    </Shell>
  );
}

/** The page's lesson: four moves, then the three kinds of test, before any list. */
function HowItWorks() {
  const t = ca.testsPage;
  return (
    <section className="card how-it-works" aria-label={t.how.title}>
      <h2>
        <i className="bi bi-signpost-2" aria-hidden="true" /> {t.how.title}
      </h2>
      <p>{t.intro}</p>
      <ol className="how">
        {t.how.steps.map((s, i) => (
          <li key={s.title}>
            <i className={`bi ${HOW_ICON[i] ?? 'bi-dot'}`} aria-hidden="true" />
            <div>
              <strong>{s.title}</strong> <span>{s.text}</span>
            </div>
          </li>
        ))}
      </ol>
      <div className="kinds">
        {KINDS.map((k) => (
          <div key={k} className="kind">
            <KindChip kind={k} />
            <span className="short">{t.kindShort[k]}</span>
            <p className="small">{t.kindHint[k]}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * One task, in two columns: the tester's part first (where, how, the steps,
 * what they saw), and beside it the follow-up the platform's reviewer fills
 * (the verdict, what changed), so a person never wonders which half is theirs.
 */
/**
 * Where the lent machine is, in the lender's words: the connect the task queued
 * for their watcher once the session's mission was up, what the watcher said,
 * and what is missing on their side (the watcher closed, "es pot deixar" off).
 */
function LendStatus({ lend }: { lend: LendState }) {
  const t = ca.testsPage.lendStatus;
  let line: string;
  if (lend.released) {
    // Only a game the watcher said it opened is closed; otherwise nothing was open.
    line = lend.job === 'done' ? t.released : t.releasedIdle;
  } else if (lend.job === null) {
    line = t.waiting;
  } else if (lend.job === 'failed' || lend.job === 'cancelled') {
    line = `${t[lend.job]} ${lend.detail ?? ''}`.trim();
  } else {
    line = t[lend.job];
  }
  const waitingOnMachine = !lend.released && (lend.job === null || lend.job === 'pending');
  return (
    <>
      <h3>{t.title}</h3>
      <p role="status">{line}</p>
      {waitingOnMachine && !lend.present && <p className="muted small">{t.notPresent}</p>}
      {waitingOnMachine && lend.present && !lend.lendable && (
        <p className="muted small">{t.notLendable}</p>
      )}
    </>
  );
}

/**
 * "Obre el joc i entra" (roadmap 2026-09-26): the caller's own watcher, never
 * another account's machine, opens the game into the card's running session.
 * The same `connect` job a lend task queues for somebody else's, self-served
 * through `POST /api/watcher/jobs/mine`; shown only once the caller's own
 * watcher has been seen (`present`), with a link to the download otherwise.
 * Polls the job while it waits, the way the lend status does.
 */
function MyConnect({ sessionCode }: { sessionCode: string }) {
  const t = ca.testsPage.myConnect;
  const { me } = useAuth();
  const [present, setPresent] = useState<boolean | null>(null);
  const [job, setJob] = useState<WatcherJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchWatcherPresence()
      .then((list) => {
        if (alive) setPresent(list.some((p) => p.accountId === me.id && p.present));
      })
      .catch(() => {
        if (alive) setPresent(false);
      });
    return () => {
      alive = false;
    };
  }, [me.id]);

  useEffect(() => {
    if (!job || job.state === 'done' || job.state === 'failed' || job.state === 'cancelled') return;
    let alive = true;
    const timer = window.setInterval(() => {
      fetchMyWatcherJob(job.id)
        .then((next) => {
          if (alive) setJob(next);
        })
        .catch(() => undefined);
    }, 3000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [job]);

  const start = () =>
    queueMyWatcherJob(sessionCode)
      .then((j) => {
        setJob(j);
        setError(null);
      })
      .catch((e) => setError(errorText(e)));

  if (present === false) {
    return (
      <p className="muted small">
        {t.needsWatcher}{' '}
        <a href={WATCHER_DOWNLOAD_URL} download>
          {t.download}
        </a>
      </p>
    );
  }

  const idle = !job || job.state === 'done' || job.state === 'failed' || job.state === 'cancelled';
  return (
    <p className="row actions">
      {idle && (
        <button type="button" className="primary" onClick={start} disabled={present === null}>
          {t.button}
        </button>
      )}
      {job && (
        <span className="muted small" role="status">
          {job.state === 'failed' || job.state === 'cancelled'
            ? `${t[job.state]} ${job.detail ?? ''}`.trim()
            : t[job.state]}
        </span>
      )}
      {error && (
        <span className="error small" role="alert">
          {error}
        </span>
      )}
    </p>
  );
}

function TaskDetail({
  task,
  uploads,
  isAdmin,
  onChanged,
  onSaved,
  onError,
  onBack,
}: {
  task: TestTask;
  uploads: boolean;
  isAdmin: boolean;
  onChanged: (t: TestTask) => void;
  /** What a box that saves itself got back: the test as it is now, with no list to read again. */
  onSaved: (t: TestTask) => void;
  onError: (e: string | null) => void;
  onBack: () => void;
}) {
  const t = ca.testsPage;
  const { me } = useAuth();
  const [verdict, setVerdict] = useState('');
  const [closeNote, setCloseNote] = useState('');
  const [session, setSession] = useState<SessionFull | null>(null);
  const [busy, setBusy] = useState<ServerBusy | null>(null);
  const canAct = task.state === 'open' || task.state === 'claimed';
  const yours = task.mine || isAdmin;
  // A web card: its steps are done in the browser, and nothing of the game is on its page.
  const isWeb = task.medium === 'web';
  // Whoever took the test, an admin and everybody who played its session write on
  // it until the review closes it (the owner, 2026-09-30); the platform says who.
  const canEdit = task.canWrite ?? (yours && (task.state === 'claimed' || task.state === 'done'));
  const save = (stepId: string, body: { answer?: StepAnswer; note?: string }) =>
    editTestStep(
      task.id,
      stepId,
      // On a web card an answer goes with where it was given from.
      isWeb && body.answer ? { ...body, context: pageState() } : body,
    ).then((next) => {
      onSaved(next);
      onError(null);
    });
  const answered = answeredSteps(task.answers);
  // An admin changes the steps of a play card nobody has taken and whose game is not
  // up with them (the platform refuses any other): how a compound step is split.
  const canChangeSteps =
    isAdmin &&
    task.kind === 'play' &&
    task.state === 'open' &&
    task.answers.length === 0 &&
    (task.server == null || task.server.state === 'ended');
  const stepsChanged = (request: Promise<TestTask>) =>
    request
      .then((next) => {
        onSaved(next);
        onError(null);
      })
      .catch((e) => onError(errorText(e)));
  const nextStep = canAct && task.steps ? task.steps.findIndex((s) => !answered.has(s.id)) : -1;
  // A web card is finished from its page once every step has the reader's own answer.
  const mineLeft = (task.steps ?? []).filter(
    (s) => !task.answers.some((a) => a.stepId === s.id && a.mine && a.answer),
  ).length;

  // The join details, from the session itself: a task never repeats them. Read
  // again when the server changes state, so the address is the one of the game that is up.
  const serverState = task.server?.state ?? null;
  useEffect(() => {
    if (!task.sessionCode) {
      setSession(null);
      return;
    }
    let alive = true;
    fetchSession(task.sessionCode)
      .then((s) => {
        if (alive) setSession(s);
      })
      .catch(() => {
        if (alive) setSession(null);
      });
    return () => {
      alive = false;
    };
  }, [task.sessionCode, serverState]);

  // Where the card's server is: off, made and waiting, starting, up, or over.
  // "Up" is the mission having reported in, not the session having started: a
  // started session with a server still loading sent a tester to an empty server.
  const sessionKnown = !!session && session.session.code === task.sessionCode;
  const phase =
    task.kind === 'play' ? cardServerPhase(task, sessionKnown ? session : null) : 'none';
  // A session of the card's own (it has a mission): finishing the card stops its server.
  const ownSession = !!task.missionCode;
  const serverUp = phase === 'starting' || phase === 'ready';

  const run = (p: Promise<TestTask>) =>
    p
      .then((next) => {
        onChanged(next);
        onError(null);
      })
      .catch((e) =>
        onError(e instanceof Error && e.message.startsWith('upload_') ? e.message : errorText(e)),
      );

  // Another night holds this card's server: said with the card it belongs to and,
  // for whoever may, a button that ends it and starts this one (the owner,
  // 2026-10-05: a bare session code left him to find and end it by hand).
  const startServer = (endOther = false) =>
    startTestServer(task.id, endOther)
      .then((next) => {
        onChanged(next);
        setBusy(null);
        onError(null);
      })
      .catch((e) => {
        const held =
          e instanceof ApiError && e.code === 'server_busy' ? (e.body as ServerBusy | null) : null;
        if (held?.sessionCode) {
          setBusy(held);
          onError(null);
        } else {
          onError(errorText(e));
        }
      });
  const endOtherAndStart = () => {
    if (!busy || !window.confirm(t.serverBusyConfirm(busy.sessionCode))) return;
    void startServer(true);
  };

  // A cancelled card, deleted for good: asked once, then back to the list.
  const remove = () => {
    if (!window.confirm(t.deleteConfirm(task.title))) return;
    deleteTest(task.id)
      .then(() => {
        onError(null);
        onBack();
      })
      .catch((e) => onError(errorText(e)));
  };

  // "Ja he acabat" on a card whose server is up takes it down for everybody in it: asked once.
  const finish = () => {
    if (ownSession && serverUp && !window.confirm(t.server.confirmDone)) return;
    void run(finishTest(task.id, {}));
  };
  const openDraft = () =>
    run(
      openTestDraft(task.id).then((next) => {
        void loadWebCards(true);
        return next;
      }),
    );

  return (
    <>
      <p>
        <button type="button" className="link" onClick={onBack}>
          ← {t.back}
        </button>
      </p>
      <header className="card task-head" aria-label={task.title}>
        <h2>
          {task.title} {isWeb ? <WebChip /> : <KindChip kind={task.kind} />}{' '}
          {task.draft ? (
            <span className="chip draft">{t.web.draft}</span>
          ) : (
            <span className={`chip ${task.state === 'open' ? 'ok' : ''}`}>
              {t.states[task.state]}
            </span>
          )}{' '}
          <PerformerChip performer={task.performer ?? 'person'} />
          {task.audience === 'admins' && (
            <>
              {' '}
              <span className="chip">
                <i className="bi bi-shield-lock" aria-hidden="true" />
                {t.web.audienceAdmins}
              </span>
            </>
          )}
        </h2>
        <p className="muted small">
          {isWeb ? t.web.short : t.kindShort[task.kind]}
          {' · '}
          {t.minutes(task.durationMinutes)}
          {task.needed > 1 ? ` · ${t.needed(task.needed)}` : ''}
          {task.track ? ` · ${t.track} ${t.trackNames[task.track] ?? task.track}` : ''}
          {/* Whether Discord was told is the organiser's question, not the tester's. */}
          {isAdmin && !task.draft ? ` · ${task.announced ? t.announced : t.notAnnounced}` : ''}
          {isAdmin && task.roadmapItem ? ` · ${t.web.roadmapItem(task.roadmapItem)}` : ''}
        </p>
        {task.brief && <p>{task.brief}</p>}
        {task.olderVersion && task.platformVersion && canAct && (
          <p className="warn small" role="note">
            {t.web.olderVersion(task.platformVersion)}
          </p>
        )}
        {task.draft && isAdmin && (
          <p className="row actions">
            <button type="button" className="primary" onClick={openDraft}>
              <i className="bi bi-megaphone" aria-hidden="true" /> {t.web.openDraft}
            </button>
            <span className="muted small">{t.web.draftHint}</span>
          </p>
        )}
        <Stepper
          kind={task.kind}
          web={isWeb}
          state={task.state}
          steps={task.steps?.length ?? 0}
          answered={answered.size}
        />
        {task.claimedBy && (
          <p className="muted small">
            {t.claimedBy(task.claimedBy)}
            {task.mine ? ` · ${t.mine}` : ''} · {when(task.claimedAt)}
          </p>
        )}
        {task.state === 'open' && !task.draft && (
          <p className="row actions">
            <button type="button" className="primary" onClick={() => run(claimTest(task.id))}>
              {isWeb ? t.web.claim : t.claim}
            </button>
            <span className="muted small">
              {isWeb ? t.web.claimHint : (t.claimHints[task.kind] ?? t.claimHint)}
            </span>
          </p>
        )}
      </header>

      <div className="task-grid">
        <section className="card tester" aria-label={t.forYou}>
          <h2>
            <i className="bi bi-person-walking" aria-hidden="true" /> {t.forYou}
          </h2>
          <p className="muted small">{t.forYouIntro}</p>

          {!isWeb && <h3>{t.where}</h3>}
          {task.kind === 'play' && !isWeb && yours && !me.identities.includes('STEAM') && (
            <p className="warn small" role="note">
              {t.steamMissing} <Link to="/settings">{t.steamLink}</Link>
            </p>
          )}
          {task.kind === 'lend' && <p className="muted small">{t.whereLend}</p>}
          {task.kind === 'judge' && !task.sessionCode && (
            <p className="muted small">{t.whereNone}</p>
          )}
          {task.kind === 'play' && !isWeb && (
            <CardServer
              task={task}
              phase={phase}
              yours={yours}
              isAdmin={isAdmin}
              canAct={canAct}
              ownSession={ownSession}
              onStart={() => startServer()}
              onStop={() => run(stopTestServer(task.id))}
              onAssign={(missionCode) =>
                run(
                  setTestMission(task.id, {
                    missionCode,
                    panelServerId: task.panelServerId ?? undefined,
                  }),
                )
              }
            />
          )}
          {busy && (
            <div className="error" role="alert">
              <p>
                {busy.cardId != null
                  ? t.serverBusyCard(
                      busy.sessionCode,
                      busy.cardTitle ?? `#${busy.cardId}`,
                      busy.cardState ? t.states[busy.cardState] : '',
                    )
                  : t.serverBusy(busy.sessionCode)}{' '}
                {busy.cardId != null && (
                  <Link to={`/proves/${busy.cardId}`}>{t.serverBusyOpenCard}</Link>
                )}
                {busy.cardId == null && <Link to={`/s/${busy.sessionCode}`}>{t.sessionPage}</Link>}
              </p>
              {busy.canEnd ? (
                <button type="button" onClick={endOtherAndStart}>
                  {t.serverBusyEnd}
                </button>
              ) : (
                <p className="small">{t.serverBusyAsk}</p>
              )}
            </div>
          )}
          {task.sessionCode && phase !== 'ended' && (
            <>
              {/* A lent machine is joined by the platform; its owner needs the mods, not the address.
                  A server still loading has no game to join yet: the address comes when it is up. */}
              {task.kind !== 'lend' && phase !== 'starting' && (
                <JoinCard
                  server={sessionKnown ? (session?.server ?? null) : null}
                  card={task.title}
                />
              )}
              {sessionKnown && (session?.definition.modSet?.mods.length ?? 0) > 0 && (
                <LauncherMods
                  code={task.sessionCode}
                  mods={session!.definition.modSet!.mods.length}
                  dlcs={session!.definition.modSet!.dlcs?.length ?? 0}
                />
              )}
              {phase === 'ready' && yours && <MyConnect sessionCode={task.sessionCode} />}
              {task.kind !== 'lend' && (
                <p>
                  <Link to={`/s/${task.sessionCode}`}>{t.sessionPage}</Link>
                </p>
              )}
            </>
          )}

          <h3>{t.howTo}</h3>
          <ol className="how iconed">
            {(isWeb ? t.web.howTo : t.howToSteps[task.kind]).map((s, i) => (
              <li key={s}>
                <i
                  className={`bi ${(isWeb ? WEB_HOW_TO_ICON : HOW_TO_ICON[task.kind])[i] ?? 'bi-dot'}`}
                  aria-hidden="true"
                />
                <span>{s}</span>
              </li>
            ))}
          </ol>

          {/* A card a person still plays or lends for: the download is here, where
              the how-to asks for it, not a page away (the owner, 2026-09-29). A web
              card needs no watcher and no game. */}
          {canAct && !isWeb && task.kind !== 'judge' && task.performer !== 'agent' && (
            <WatcherDownload kind={task.kind} />
          )}

          {task.kind === 'lend' && task.lend && yours && <LendStatus lend={task.lend} />}

          {task.kind === 'play' && task.steps && (
            <>
              <h3>{isWeb ? t.web.steps : t.yourSteps}</h3>
              {isWeb ? (
                <p className="muted small">{t.web.stepsHint}</p>
              ) : (
                <>
                  <p className="keycaps" aria-hidden="true">
                    {t.keycaps.map(([key, word]) => (
                      <span key={key}>
                        <kbd className={word === 'sí' ? 'yes' : word === 'no' ? 'no' : ''}>
                          {key}
                        </kbd>
                        {word}
                      </span>
                    ))}
                  </p>
                  <p className="muted small">
                    <span className="keys vh">{t.keys}</span>
                    {t.keycapsHint} {t.autoDone}
                    {canEdit ? ` ${t.fixHint} ${t.autosaveHint}` : ''}
                  </p>
                </>
              )}
              <ol className="task-route">
                {task.steps.map((s, i) => {
                  const answers = task.answers.filter((a) => a.stepId === s.id);
                  // The reader's own word is what their boxes hold; on a platform that
                  // does not say whose an answer is, the one person a test had.
                  const own = answers.some((a) => a.mine !== undefined)
                    ? answers.filter((a) => a.mine)
                    : answers;
                  const current = [...own].reverse().find((a) => a.answer)?.answer ?? null;
                  const stepNote = [...own].reverse().find((a) => a.note)?.note ?? null;
                  const said = answers.filter((a) => a.note && !(canEdit && own.includes(a)));
                  // A web step is about a page, not about a moment of the game: no phase read from its words.
                  const phase = isWeb ? 'look' : phaseOf(s);
                  const isNext = i === nextStep;
                  const seen = answers.find((a) => a.context)?.context;
                  return (
                    <li
                      key={s.id}
                      className={`${answered.has(s.id) ? 'answered' : ''}${isNext ? ' next' : ''}`}
                    >
                      <span className={`badge phase-${phase}`} aria-hidden="true">
                        <i className={`bi ${isWeb ? 'bi-window' : PHASE_ICON[phase]}`} />
                        <span className="n">{i + 1}</span>
                      </span>
                      <div>
                        <span className="phase-label">
                          {isWeb ? t.web.stepOf(i + 1, task.steps?.length ?? 0) : t.phases[phase]}
                          {isWeb && s.role ? ` · ${t.web.forRole(s.role)}` : ''}
                          {isNext && <span className="next-tag">{t.nextStep}</span>}
                        </span>
                        <div className="step-text">{s.text}</div>
                        {isWeb && isAppPath(s.url) && (
                          <p className="step-open">
                            <Link
                              className="button small"
                              to={s.url}
                              title={t.web.openTitle(s.url)}
                            >
                              <i className="bi bi-box-arrow-up-right" aria-hidden="true" />{' '}
                              {t.web.open}
                            </Link>
                          </p>
                        )}
                        {isAdmin && <StepWarnings warnings={task.stepWarnings?.[s.id]} />}
                        {s.expect && (
                          <div className="expect">
                            <i className="bi bi-binoculars" aria-hidden="true" />
                            {t.stepExpect} {s.expect}
                          </div>
                        )}
                        {s.goodAnswer === 'no' && <div className="expect">{t.stepGoodNo}</div>}
                        {answers.map((a, j) =>
                          a.answer ? (
                            <span
                              key={j}
                              className={`chip ${
                                a.answer === 'cant'
                                  ? 'warn'
                                  : a.answer === (s.goodAnswer ?? 'yes')
                                    ? 'ok'
                                    : 'bad'
                              }`}
                            >
                              {answerWord(a.answer)}
                              {(a.by ?? a.player) ? ` · ${a.by ?? a.player}` : ''}
                              {a.reportId ? ` · ${t.report}` : ''}
                              {a.edited
                                ? ` ${a.originalAnswer ? t.edited(answerWord(a.originalAnswer)) : t.editedNoGame}`
                                : ''}
                            </span>
                          ) : null,
                        )}
                        {said.map((a, j) => (
                          <p className="step-note" key={`n${j}`}>
                            <i className="bi bi-chat-left-text" aria-hidden="true" />{' '}
                            {(a.by ?? a.player) ? <strong>{a.by ?? a.player}</strong> : null}{' '}
                            {a.note}
                          </p>
                        ))}
                        {isAdmin && seen && (
                          <p className="muted small step-context">
                            <i className="bi bi-window" aria-hidden="true" /> {contextLine(seen)}
                          </p>
                        )}
                        {canEdit && (
                          <StepEditor
                            current={current}
                            note={stepNote}
                            onSave={(body) => save(s.id, body)}
                          />
                        )}
                        {canChangeSteps && (task.steps?.length ?? 0) > 1 && (
                          <button
                            type="button"
                            className="link small"
                            onClick={() => stepsChanged(removeTestStep(task.id, s.id))}
                          >
                            <i className="bi bi-trash" aria-hidden="true" /> {t.stepRemove}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ol>
              {canChangeSteps && (
                <AddStep
                  steps={task.steps}
                  onAdd={(step, after) => stepsChanged(addTestStep(task.id, step, after))}
                />
              )}
              {!isWeb && task.answers.length === 0 && <p className="muted small">{t.noAnswers}</p>}
              {canAct && yours && !isWeb && (
                <p className="row actions">
                  <button type="button" onClick={finish}>
                    {t.done}
                  </button>
                  <span className="muted small">
                    {ownSession && serverUp ? t.doneStopsHint : t.doneHint}
                  </span>
                </p>
              )}
              {isWeb && task.state === 'claimed' && yours && (
                <p className="row actions">
                  <button
                    type="button"
                    className="primary"
                    disabled={mineLeft > 0}
                    onClick={finish}
                  >
                    {t.web.finish}
                  </button>
                  <span className="muted small">
                    {mineLeft > 0 ? t.web.finishMissing(mineLeft) : t.web.finishHint}
                  </span>
                </p>
              )}
            </>
          )}

          {task.kind === 'judge' && task.question && (
            <>
              <h3>{t.question}</h3>
              {task.question.pictureUrl && (
                <img src={task.question.pictureUrl} alt="" style={{ maxWidth: '100%' }} />
              )}
              <p>{task.question.text}</p>
              {task.result?.option ? (
                <p>
                  <strong>{t.option(task.result.option)}</strong>
                </p>
              ) : task.state === 'open' && !isAdmin ? (
                <p className="muted small">{t.claimFirst}</p>
              ) : (
                canAct && (
                  <p className="row actions">
                    {task.question.options.map((o) => (
                      <button
                        key={o}
                        type="button"
                        className="primary"
                        onClick={() => run(finishTest(task.id, { option: o }))}
                      >
                        {o}
                      </button>
                    ))}
                  </p>
                )
              )}
            </>
          )}

          {(canEdit || (task.notes?.length ?? 0) > 0) && (
            <>
              <h3>{t.whatSeen}</h3>
              <p className="muted small">{t.whatSeenHint}</p>
            </>
          )}
          <CardNotes
            notes={task.notes ?? []}
            canWrite={canEdit}
            onSave={(text) => save(CARD_STEP, { note: text })}
          />
          {task.state !== 'cancelled' && canEdit && (
            <>
              <p className="row actions">
                {uploads ? (
                  <label className="chip">
                    {t.dropFile}
                    <input
                      type="file"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void run(uploadTestFile(task.id, file));
                      }}
                    />
                  </label>
                ) : (
                  <span className="muted small">{t.uploadsOff}</span>
                )}
              </p>
            </>
          )}
          {task.entries.length > 0 && (
            <ul className="activity" aria-label={t.entries}>
              {task.entries.map((e) => (
                <li key={e.id}>
                  <span className="muted small">{when(e.at)}</span>{' '}
                  {e.by ? <strong>{e.by}</strong> : null}{' '}
                  {e.kind === 'file' && e.url ? (
                    <a href={e.url} target="_blank" rel="noreferrer">
                      {e.fileName ?? t.file}
                    </a>
                  ) : (
                    e.body
                  )}
                </li>
              ))}
            </ul>
          )}
          <GameReports reports={task.reports ?? []} />
        </section>

        <aside className="card followup" aria-label={t.followUp}>
          <h2>
            <i className="bi bi-clipboard-data" aria-hidden="true" /> {t.followUp}
          </h2>
          <p className="muted small">{t.followUpIntro}</p>
          {task.sessionCode && isAdmin && (
            <>
              <p>
                <Link to={`/s/${task.sessionCode}/tecnic`}>{t.timelineLink}</Link>
              </p>
              <p>
                <Link to={`/proves/inspeccio/${task.sessionCode}`}>
                  {ca.inbox.capturesTitle(task.sessionCode)}
                </Link>
              </p>
            </>
          )}
          {isAdmin && (
            <p className="row actions">
              <button
                type="button"
                className="small"
                onClick={() =>
                  run(
                    setTestPerformer(
                      [task.id],
                      (task.performer ?? 'person') === 'agent' ? 'person' : 'agent',
                    ).then((changed) => changed[0] ?? task),
                  )
                }
              >
                <i className="bi bi-arrow-left-right" aria-hidden="true" /> {t.performerChange}
              </button>
            </p>
          )}
          <h3>{t.closedNote}</h3>
          {task.verdict && (
            <p>
              <span className="chip">{task.verdict}</span>
            </p>
          )}
          {task.closedNote ? <p>{task.closedNote}</p> : <p className="muted small">{t.pending}</p>}
          {isAdmin && task.state !== 'closed' && task.state !== 'cancelled' && (
            <div className="admin-close">
              <h3>{t.close}</h3>
              <label>
                {t.verdict}
                <input
                  className="control"
                  value={verdict}
                  onChange={(e) => setVerdict(e.target.value)}
                />
              </label>
              <label>
                {t.closedNote}
                <input
                  className="control"
                  value={closeNote}
                  onChange={(e) => setCloseNote(e.target.value)}
                  placeholder={t.closeNote}
                />
              </label>
              <p className="row actions">
                <button
                  type="button"
                  className="primary"
                  onClick={() => run(closeTest(task.id, verdict, closeNote))}
                >
                  {t.close}
                </button>
                <button type="button" onClick={() => run(cancelTest(task.id))}>
                  {t.cancel}
                </button>
              </p>
            </div>
          )}
          {isAdmin && task.state === 'cancelled' && (
            <div className="admin-close">
              <h3>{t.deleteTitle}</h3>
              <p className="muted small">{t.deleteHint}</p>
              <p className="row actions">
                <button type="button" className="danger" onClick={remove}>
                  {t.deleteButton}
                </button>
              </p>
            </div>
          )}
        </aside>
      </div>
    </>
  );
}

/** The admin's own way of writing a task; the agent uses the same request through the API. */
function CreateForm({
  onCreated,
  onError,
}: {
  onCreated: (t: TestTask) => void;
  onError: (e: string | null) => void;
}) {
  const t = ca.testsPage;
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<TestKind>('play');
  const [title, setTitle] = useState('');
  const [brief, setBrief] = useState('');
  const [track, setTrack] = useState('');
  const [session, setSession] = useState('');
  const [duration, setDuration] = useState(30);
  const [needed, setNeeded] = useState(1);
  const [steps, setSteps] = useState('');
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState('');
  const [performer, setPerformer] = useState<TestPerformer>('person');
  const [medium, setMedium] = useState<TestMedium>('game');
  const [audience, setAudience] = useState<TestAudience>('testers');
  const [roadmapItem, setRoadmapItem] = useState('');
  const [draft, setDraft] = useState(false);
  const web = kind === 'play' && medium === 'web';

  function submit(event: FormEvent) {
    event.preventDefault();
    const body: CreateTest = {
      kind,
      title,
      brief: brief || undefined,
      track: track || undefined,
      // A web card is about no session: its steps are done in the browser.
      sessionCode: web ? undefined : session || undefined,
      durationMinutes: duration,
      needed,
      performer,
      medium: web ? 'web' : 'game',
      audience,
      roadmapItem: roadmapItem.trim() || undefined,
      draft,
    };
    if (kind === 'play') body.steps = parseSteps(steps);
    if (kind === 'judge') {
      body.question = {
        text: question,
        options: options
          .split('\n')
          .map((o) => o.trim())
          .filter(Boolean),
      };
    }
    createTest(body)
      .then((created) => {
        onError(null);
        onCreated(created);
      })
      .catch((e) => onError(errorText(e)));
  }

  if (!open)
    return (
      <p>
        <button type="button" onClick={() => setOpen(true)}>
          <i className="bi bi-plus-lg" aria-hidden="true" /> {t.create}
        </button>
      </p>
    );
  return (
    <form className="card form" onSubmit={submit} aria-label={t.create}>
      <h2>{t.create}</h2>
      <p className="row">
        {(['play', 'lend', 'judge'] as TestKind[]).map((k) => (
          <label key={k} className="chip">
            <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />{' '}
            {t.createKinds[k]}
          </label>
        ))}
      </p>
      <fieldset className="row">
        <legend>{t.createPerformer}</legend>
        {(['person', 'agent'] as TestPerformer[]).map((p) => (
          <label key={p} className="chip">
            <input
              type="radio"
              name="performer"
              checked={performer === p}
              onChange={() => setPerformer(p)}
            />{' '}
            {ca.inbox.performers[p]}
          </label>
        ))}
      </fieldset>
      <p className="muted small">{t.createPerformerHint}</p>
      {kind === 'play' && (
        <fieldset className="row">
          <legend>{t.web.createMedium}</legend>
          {(['game', 'web'] as TestMedium[]).map((m) => (
            <label key={m} className="chip">
              <input
                type="radio"
                name="medium"
                checked={medium === m}
                onChange={() => setMedium(m)}
              />{' '}
              {t.web.mediums[m]}
            </label>
          ))}
        </fieldset>
      )}
      <fieldset className="row">
        <legend>{t.web.createAudience}</legend>
        {(['testers', 'admins'] as TestAudience[]).map((a) => (
          <label key={a} className="chip">
            <input
              type="radio"
              name="audience"
              checked={audience === a}
              onChange={() => setAudience(a)}
            />{' '}
            {t.web.audiences[a]}
          </label>
        ))}
      </fieldset>
      <label>
        {t.web.createRoadmapItem}
        <input value={roadmapItem} onChange={(e) => setRoadmapItem(e.target.value)} />
      </label>
      <label className="chip">
        <input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)} />{' '}
        {t.web.createDraft}
      </label>
      <label>
        {t.createTitle}
        <input value={title} onChange={(e) => setTitle(e.target.value)} required />
      </label>
      <label>
        {t.createBrief}
        <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} />
      </label>
      <label>
        {t.createTrack}
        <input value={track} onChange={(e) => setTrack(e.target.value)} />
      </label>
      {kind !== 'judge' && !web && (
        <label>
          {t.createSession}
          <input value={session} onChange={(e) => setSession(e.target.value)} />
        </label>
      )}
      <label>
        {t.createDuration}
        <input
          type="number"
          min={1}
          value={duration}
          onChange={(e) => setDuration(Number(e.target.value))}
        />
      </label>
      <label>
        {t.createNeeded}
        <input
          type="number"
          min={1}
          value={needed}
          onChange={(e) => setNeeded(Number(e.target.value))}
        />
      </label>
      {kind === 'play' && (
        <label>
          {t.createSteps}
          <textarea value={steps} onChange={(e) => setSteps(e.target.value)} rows={5} required />
          <span className="muted small">{t.createStepsRules}</span>
          {web && <span className="muted small">{t.web.createStepsUrl}</span>}
        </label>
      )}
      {kind === 'judge' && (
        <>
          <label>
            {t.createQuestion}
            <input value={question} onChange={(e) => setQuestion(e.target.value)} required />
          </label>
          <label>
            {t.createOptions}
            <textarea
              value={options}
              onChange={(e) => setOptions(e.target.value)}
              rows={3}
              required
            />
          </label>
        </>
      )}
      <p className="row actions">
        <button type="submit" className="primary">
          {t.createSubmit}
        </button>
        <button type="button" onClick={() => setOpen(false)}>
          {ca.cancel}
        </button>
      </p>
    </form>
  );
}

/**
 * A batch of cards from a file (docs/verification/web-cards-*.json): every one is
 * made as a draft, so the owner reads them before anybody is offered or told
 * anything. Opening them is the inbox's «Obre-les totes», or each card's «Obre-la».
 */
function ImportCards({ onError }: { onError: (e: string | null) => void }) {
  const t = ca.testsPage.web;
  const [done, setDone] = useState<string | null>(null);

  const onFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setDone(null);
    file
      .text()
      .then((text) => {
        let cards: unknown;
        try {
          cards = JSON.parse(text);
        } catch {
          cards = null;
        }
        if (!Array.isArray(cards)) throw new Error(t.importBad);
        return importTests(cards as ImportCard[]);
      })
      .then((result) => {
        onError(null);
        setDone(t.importDone(result.created.length, result.skipped.length));
      })
      .catch((e) =>
        onError(e instanceof Error && e.message === t.importBad ? e.message : errorText(e)),
      );
  };

  return (
    <section className="import-cards" aria-label={t.importTitle}>
      <h2>
        <i className="bi bi-file-earmark-arrow-up" aria-hidden="true" /> {t.importTitle}
      </h2>
      <p className="muted small">{t.importHint}</p>
      <label>
        {t.importFile}
        <input type="file" accept="application/json,.json" onChange={onFile} />
      </label>
      {done && (
        <p role="status">
          {done} <Link to="/proves?safata=todo">{ca.inbox.trays.todo}</Link>
        </p>
      )}
    </section>
  );
}
